<script setup lang="ts">
import { useModel } from './../model/model'
import { VModal } from '@/shared/ui'
import { computed, type PropType, ref, watchEffect } from 'vue'
import { useHelpers, useTranslate } from '@/shared/lib'
import type { AdminConfiguratorEventsType, AdminConfiguratorType } from '@/entities/Cabinet'
import { useConfiguratorSave } from '@/features/Cabinet/AdminConfigurator/model/api'
import { useRoute } from 'vue-router'

const props = defineProps({
  items: {
    type: Array as PropType<AdminConfiguratorType[]>,
    required: true,
  },
  events: {
    type: Array as PropType<AdminConfiguratorEventsType[]>,
    required: true,
  },
})

const { trs } = useTranslate()
const { numberList } = useHelpers()
const model = useModel()
const route = useRoute()
const emits = defineEmits(['updated'])

const { save, isPending } = useConfiguratorSave(Number(route.params.object_id))

const numbers = numberList(63)

/**
 * Вычисляем заголовок в зависимости от создания/обновления
 */
const title = computed(() => {
  return model.data.id ? trs('itf.config_edit') : trs('itf.config_add')
})

/**
 * Вычисляем свободные номера входов
 */
const pinNumbers = computed(() => {
  const excludeNumbers = props.items.map((item) => item.pin_number)
  return numbers.filter((item) => !excludeNumbers.includes(item))
})

/**
 * Назначаем свободный вход по умолчанию
 */
watchEffect(() => {
  if (model.data.pin_number === null || model.data.pin_number === undefined) {
    model.data.pin_number = pinNumbers.value[0]
  }
})

/**
 * Вычисляем свободные события
 */
const freeEvents = computed(() => {
  const excludeEvents = new Set(props.items.map((item) => item.device_event_type_id))
  excludeEvents.delete(4)
  excludeEvents.delete(Number(model.data.device_event_type_id))

  return props.events.filter((item) => !excludeEvents.has(item.id))
})

const valid = ref(false)

const submitHandler = async () => {
  if (!valid.value) return

  save(model.data).then(() => {
    emits('updated')
    model.closeForm()
  })
}
</script>

<template>
  <VModal :title v-model="model.openForm">
    <VForm v-model="valid" @submit.prevent="submitHandler" class="mt-2">
      <VSelect :label="$t('itf.pin_number')" v-model="model.data.pin_number" :items="pinNumbers" />

      <VSelect
        :label="$t('itf.type')"
        v-model="model.data.device_event_type_id"
        :items="freeEvents"
        item-value="id"
        item-title="event_type"
      />

      <VTextarea :label="$t('itf.description')" rows="3" v-model="model.data.description" />

      <div class="form__btn_right">
        <VBtn type="submit" :loading="isPending">
          {{ model.data.id ? $t('itf.save') : $t('itf.add') }}
        </VBtn>

        <VBtn color="default" @click="model.closeForm">{{ $t('itf.cancel') }}</VBtn>
      </div>
    </VForm>
  </VModal>
</template>
